import {createAsyncThunk, createAction, unwrapResult} from '@reduxjs/toolkit';
import Api, {ApiMethods} from '../../../middleware/api';
import {notify} from '../../notifier/services/actions';
import {getProjectById} from './selectors';
import {ProjectFeedbacksDto, ProjectTeamDto} from '../typedef';
import {FeedbackDto} from '../../../dto/FeedbackDto';
import {ProfileDto} from '../../../dto/ProfileDto';
import {ProjectInfoDto} from '../../../dto/ProjectInfoDto';
import {MarkTemplateDto} from '../../../dto/MarkTemplateDto';
import {SearchProfileDto} from '../../../dto/SearchProfileDto';
import {RelationType} from '../../../dto/RelationDto';
import {ViewDto} from '../../../dto/ViewDto';
import {RootState} from 'feedback-system/reducers';

type Args = {
    profileId: number;
    periodId: number;
    projectId: number;
};

type SaveFeedbacksArgs = {
    projectId: number;
    feedbacks: FeedbackDto[];
};

type CreateRelationArgs = {
    periodId: number;
    fromProfileId: number;
    projectId: number;
    toProfileId: number;
    type?: RelationType;
    projectName?: string;
};

export const getProjects = createAsyncThunk(
    'qualityFeedback/fetchProjects',
    async (_, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: 'project/quality-projects-info',
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return responseData as ProjectInfoDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getProfiles = createAsyncThunk<ProjectTeamDto, Args>(
    'qualityFeedback/fetchProfiles',
    async ({profileId, periodId, projectId}, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `profiles/team/${profileId}?periodId=${periodId}&projectId=${projectId}`,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return {projectId, team: responseData} as ProjectTeamDto;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getLastViewProfiles = createAsyncThunk<ProfileDto[], number[]>(
    'qualityFeedback/getLastViewProfiles',
    async (profileIds, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: 'profiles/',
            method: ApiMethods.POST,
            body: profileIds
        });
        if (httpStatus === 200) {
            return responseData;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getFeedbacks = createAsyncThunk<ProjectFeedbacksDto, Args>(
    'qualityFeedback/fetchFeedbacks',
    async ({profileId, periodId, projectId}, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `feedback/latest/${profileId}?periodId=${periodId}&projectId=${projectId}`,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return {projectId, feedbacks: responseData} as ProjectFeedbacksDto;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getLastViewFeedbacks = createAsyncThunk<
    FeedbackDto[],
    {projectId: number; recipientId: number; feedbackIds: number[]}
>(
    'qualityFeedback/getLastViewFeedbacks',
    async ({projectId, recipientId, feedbackIds}, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: 'feedback/',
            method: ApiMethods.POST,
            body: feedbackIds
        });
        if (httpStatus === 200) {
            return responseData;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getLastView = createAsyncThunk<ViewDto[], Args>(
    'qualityFeedback/getLastView',
    async ({profileId, periodId, projectId}, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `view/${profileId}?periodId=${periodId}&projectId=${projectId}`,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return responseData;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const updateLastView = createAsyncThunk(
    'qualityFeedback/updateLastView',
    async (viewId: number, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `view/${viewId}`,
            method: ApiMethods.PUT
        });
        if (httpStatus === 201) {
            return viewId;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const deleteFeedback = createAsyncThunk<
    {feedbackId: number; projectId: number},
    {feedbackId: number; projectId: number}
>('qualityFeedback/deleteFeedback', async (args, {dispatch, rejectWithValue, requestId}) => {
    const {httpStatus, responseData} = await Api.fetch({
        endpoint: `feedback/${args.feedbackId}`,
        method: ApiMethods.DELETE
    });
    if (httpStatus === 200) {
        return args;
    }
    dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
    return rejectWithValue(responseData);
});

export const updateFeedbacks = createAction<FeedbackDto[]>('qualityFeedback/updateFeedbacks');

export const autoSaveFeedbacks = createAsyncThunk(
    'qualityFeedback/autoSaveFeedbacks',
    async ({projectId, feedbacks}: SaveFeedbacksArgs, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `feedback/auto-save/${projectId}`,
            method: ApiMethods.PUT,
            body: feedbacks
        });
        if (httpStatus === 201) {
            dispatch(updateFeedbacks(responseData));
            return responseData as FeedbackDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const submitFeedbacks = createAsyncThunk(
    'qualityFeedback/submitFeedbacks',
    async ({projectId, feedbacks}: SaveFeedbacksArgs, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `feedback/submit/${projectId}`,
            method: ApiMethods.PUT,
            body: feedbacks
        });
        if (httpStatus === 201) {
            dispatch(updateFeedbacks(responseData));
            dispatch(notify({message: 'Submitted', key: requestId, variant: 'success'}));
            return responseData as FeedbackDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getTemplates = createAsyncThunk<
    MarkTemplateDto[],
    {
        projectId: number;
        positions: string[];
    }
>('qualityFeedback/fetchTemplates', async ({projectId, positions}, {dispatch, rejectWithValue, requestId}) => {
    const {httpStatus, responseData} = await Api.fetch({
        endpoint: `mark/template/${projectId}`,
        method: ApiMethods.POST,
        body: positions
    });
    if (httpStatus === 200) {
        return responseData as MarkTemplateDto[];
    }
    dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
    return rejectWithValue(responseData);
});

export const searchProfilesWithProjects = createAsyncThunk<SearchProfileDto[], string>(
    'qualityFeedback/fetchProfilesWithProjects',
    async (query, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `profiles/search?query=${query}`,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return responseData as SearchProfileDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const createRelation = createAsyncThunk<number, CreateRelationArgs>(
    'qualityFeedback/createRelation',
    async ({periodId, ...relation}, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `relation/save/${periodId}`,
            method: ApiMethods.POST,
            body: relation
        });
        if (httpStatus === 201) {
            return responseData;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const deleteRelation = createAsyncThunk(
    'qualityFeedback/deleteRelation',
    async (relationId: number, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `relation/delete/${relationId}`,
            method: ApiMethods.DELETE
        });
        if (httpStatus === 200) {
            return responseData;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const createOptionalFeedback = createAsyncThunk<ProjectInfoDto, CreateRelationArgs>(
    'qualityFeedback/createOptionalFeedback',
    async (
        {fromProfileId, toProfileId, projectId, periodId, projectName},
        {dispatch, getState, rejectWithValue, requestId}
    ) => {
        const relation = {fromProfileId, projectId, toProfileId, type: RelationType.OPTIONAL};
        const resultAction = await dispatch(createRelation({...relation, periodId}));
        if (createRelation.fulfilled.match(resultAction)) {
            const project = getProjectById(projectId)(getState() as RootState);
            const currentPeriodId = Number(sessionStorage.getItem(String(projectId)));
            if (project) {
                if (periodId === currentPeriodId) {
                    dispatch(getProfiles({profileId: fromProfileId, periodId, projectId}));
                }
                return ([] as unknown) as ProjectInfoDto;
            } else {
                const {httpStatus, responseData} = await Api.fetch({
                    endpoint: `periods/project/${projectId}`,
                    method: ApiMethods.GET
                });
                if (httpStatus === 200) {
                    return {projectId, projectName, periods: responseData} as ProjectInfoDto;
                }
                dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
                return rejectWithValue(responseData);
            }
        } else {
            return rejectWithValue('Create relation error');
        }
    }
);

export const deleteOptionalFeedback = createAsyncThunk<
    {relationId: number; projectId: number},
    {relationId: number; projectId: number}
>('qualityFeedback/deleteOptionalFeedback', async (args, {dispatch, rejectWithValue, requestId}) => {
    const resultAction = await dispatch(deleteRelation(args.relationId));
    if (deleteRelation.fulfilled.match(resultAction)) {
        dispatch(notify({message: 'Success', key: requestId, variant: 'success'}));
        return args;
    } else {
        return rejectWithValue('Delete relation error');
    }
});

export const clearProfilesWithProjects = createAction('qualityFeedback/clearProfilesWithProjects');
